import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Settings } from "lucide-react";
import { Link, useParams } from "react-router";
import DarkModeToggle from "../../components/DarkModeToggle";
import ReportConfiguration from "../../components/ReportConfiguration";
import ReportDisplay from "../../components/ReportDisplay";
import ReportLoading from "../../components/ReportLoading";
import ReportPlan from "../../components/ReportPlan";
import { usePollTask } from "../../hooks/usePollTask";
import { useReportById } from "../../hooks/useReportHistory";
import { useTrackPageView } from "../../hooks/useTrackPageView";

function Report() {
  const { id } = useParams();
  useTrackPageView("Report", { report_id: id });
  const reportQuery = useReportById(id);
  const shouldPoll =
    reportQuery.status !== "pending" &&
    reportQuery.data?.status !== "complete" &&
    reportQuery.data?.status !== "failed";
  const reportPollQuery = usePollTask(id, shouldPoll);
  const reportFromHistory = shouldPoll ? reportPollQuery.data : reportQuery.data;
  const stage = reportFromHistory?.status;

  if (!id) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-8 max-w-4xl">
          <Alert className="mt-8">
            <AlertTitle>No Report ID</AlertTitle>
            <AlertDescription>
              <p>No report ID was provided.</p>
              <Button asChild className="mt-4">
                <Link to="/v1/">Create New Report</Link>
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (reportPollQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-8 max-w-4xl">
          <ReportLoading stage="init" message="Loading report..." />
        </div>
      </div>
    );
  }

  if (!reportFromHistory) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-8 max-w-4xl">
          <Alert className="mt-8">
            <AlertTitle>Report Not Found</AlertTitle>
            <AlertDescription>
              <p>The requested report could not be found.</p>
              <Button asChild className="mt-4">
                <Link to="/v1/">Create New Report</Link>
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="absolute top-0 left-0 p-6 flex items-center gap-2">
        <Link to="/v1/settings">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <Settings className="h-4 w-4" />
            <span className="sr-only">Settings</span>
          </Button>
        </Link>
        <div className="h-5 w-px bg-border" />
        <DarkModeToggle />
      </div>

      <nav className="absolute top-0 right-0 p-6 flex gap-2">
        <Button variant="outline" asChild>
          <Link to="/v1/reports">View Reports</Link>
        </Button>
        <Button asChild>
          <Link to="/v1">New Report</Link>
        </Button>
      </nav>

      <div className="container mx-auto p-8 max-w-4xl pt-20">
        <ReportConfiguration report={reportFromHistory} />
        <div className="space-y-6">
          {(reportPollQuery.error || reportFromHistory?.status === "failed") && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  {reportPollQuery.error?.message || "Failed to generate report. Please try again."}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                    Try Again
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/v1">New Report</Link>
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {stage === "building" && !reportPollQuery.error && (
            <Card>
              <CardContent>
                <ReportPlan
                  progress={reportFromHistory.progress}
                  message={reportFromHistory?.progressDetails?.details}
                  plan={reportFromHistory?.simplePlan}
                />
              </CardContent>
            </Card>
          )}

          {stage === "complete" && !reportPollQuery.error && (
            <ReportDisplay report={reportFromHistory.report} />
          )}
        </div>

        {/* Debug link */}
        {import.meta.env.DEV && (
          <div className="mt-8 text-center">
            <Link
              to={`/v1/report/${id}/debug`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Debug
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Report;
